/* ==========================================================================
   CENTRAL CANAP' — Global theme behaviour
   Scroll reveal, sticky header, AJAX add-to-cart feedback, AJAX cart page
   updates. Vanilla JS only, no external library. Every enhancement here is
   progressive: the underlying Shopify forms/links keep working natively if
   JS fails, so cart / checkout / variants are never at risk.
   ========================================================================== */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------------ */
  function initReveal() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      return; // stay visible, no animation
    }

    document.documentElement.classList.add('has-reveal');

    targets.forEach(function (el) {
      el.classList.add('reveal-init');
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-init');
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Sticky header + scroll shadow
     ------------------------------------------------------------------------ */
  function initStickyHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var lastState = false;
    function update() {
      var scrolled = window.scrollY > 12;
      if (scrolled !== lastState) {
        header.classList.toggle('site-header--scrolled', scrolled);
        lastState = scrolled;
      }
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------------------------------------------------
     Toast (add to cart confirmation)
     ------------------------------------------------------------------------ */
  var toastTimeout;
  function showToast(message) {
    var toast = document.querySelector('[data-toast]');
    if (!toast) return;
    var label = toast.querySelector('[data-toast-label]');
    if (label) label.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimeout);
    toastTimeout = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2600);
  }
  window.CentralCanapeToast = showToast;

  /* ------------------------------------------------------------------------
     Cart count sync (any element with [data-cart-count])
     ------------------------------------------------------------------------ */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count <= 0;
    });

    // Discreet micro-bounce on the cart badge itself when the count grows —
    // premium, single-fire feedback (never on removal or page load), and
    // fully skipped under prefers-reduced-motion.
    if (prefersReducedMotion) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.classList.remove('is-bumping');
      void el.offsetWidth;
      el.classList.add('is-bumping');
      window.setTimeout(function () {
        el.classList.remove('is-bumping');
      }, 320);
    });
  }

  /* ------------------------------------------------------------------------
     AJAX "add to cart" progressive enhancement.
     Any <form action="/cart/add" ...> (Shopify's {% form 'product' %}) is
     intercepted: on success we update the header cart count + show a toast
     instead of a full page reload. If the fetch fails for any reason, the
     form's native submit (already in flight from the browser) still lands
     on Shopify's own /cart/add endpoint and behaves exactly as before.
     ------------------------------------------------------------------------ */
  function initAjaxAddToCart() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form.matches('form[action*="/cart/add"]')) return;

      var submitBtn = form.querySelector('[data-add-to-cart]') || form.querySelector('button[type="submit"], input[type="submit"]');
      if (!submitBtn || submitBtn.disabled) return;

      event.preventDefault();

      // Any product form on the page may also have a mobile sticky-bar CTA
      // that triggers this same form (it never has a form of its own) — keep
      // both buttons in the same disabled/loading state so a double add via
      // the two buttons is impossible.
      var mobileBarCta = document.querySelector('[data-mobile-bar-cta]');
      var labelEl = submitBtn.querySelector('[data-add-to-cart-label]');
      var loadingLabel = submitBtn.getAttribute('data-label-loading');
      var availableLabel = submitBtn.getAttribute('data-label-available');
      var addedLabel = submitBtn.getAttribute('data-label-added');
      var errorEl = form.querySelector('[data-cart-error]');

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      form.classList.add('is-loading');
      submitBtn.classList.remove('is-added');
      if (submitBtn.tagName === 'BUTTON') submitBtn.disabled = true;
      if (mobileBarCta) mobileBarCta.disabled = true;
      if (labelEl && loadingLabel) labelEl.textContent = loadingLabel;

      var formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              var apiError = new Error('add-to-cart-api-error');
              apiError.isApiError = true;
              apiError.data = data;
              throw apiError;
            }
            return data;
          });
        })
        .then(function (item) {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              updateCartCount(cart.item_count);
              showToast((item.product_title || 'Produit') + ' ajouté au panier');

              // Brief "Ajouté ✓" success state on the button itself before
              // it reverts to its normal label — purely additive, and only
              // when the button actually declares one via
              // data-label-added (older/other add-to-cart buttons without
              // that attribute keep their previous available/loading-only
              // behaviour, unchanged).
              if (labelEl && addedLabel) {
                labelEl.textContent = addedLabel;
                submitBtn.classList.add('is-added');
                window.setTimeout(function () {
                  submitBtn.classList.remove('is-added');
                  if (labelEl && availableLabel) labelEl.textContent = availableLabel;
                }, 1600);
              }
            });
        })
        .catch(function (err) {
          // A real Shopify response (e.g. sold out, combination unavailable)
          // — show the message inline, no page reload, no silent console-only
          // failure.
          if (err && err.isApiError) {
            var message = (err.data && (err.data.description || err.data.message)) || "Cette combinaison n'est actuellement pas disponible.";
            if (errorEl) {
              errorEl.textContent = message;
              errorEl.hidden = false;
            }
            return;
          }
          // Network-level failure only (fetch itself never completed): fall
          // back to a native submit so the add-to-cart still happens.
          // (.submit() does NOT re-dispatch the 'submit' event, so this can't loop.)
          HTMLFormElement.prototype.submit.call(form);
        })
        .finally(function () {
          form.classList.remove('is-loading');
          if (submitBtn.tagName === 'BUTTON') submitBtn.disabled = false;
          if (mobileBarCta) mobileBarCta.disabled = false;
          // Only restore the "available" label immediately when no
          // "added" success phase is running — that phase manages its own
          // revert on its own timer (see above) so it isn't cut short.
          if (!(labelEl && addedLabel && submitBtn.classList.contains('is-added'))) {
            if (labelEl && availableLabel) labelEl.textContent = availableLabel;
          }
        });
    });
  }

  /* ------------------------------------------------------------------------
     AJAX cart page: quantity steppers + remove, backed by /cart/change.js.
     The page still contains a fully working native <form action="/cart">
     with real name="updates[]" inputs, so if JS is unavailable the merchant
     can still change quantities and submit normally to reach the same
     result — this layer only makes it feel instant.
     ------------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    // Uses the store's own money format string, provided by Liquid as
    // window.CentralCanapeMoneyFormat, so figures always match Shopify's
    // real currency settings instead of a hardcoded "€".
    var value = (cents / 100).toFixed(2).replace('.', ',');
    var fmt = format || (window.CentralCanapeMoneyFormat || '{{amount}} €');
    return fmt.replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function refreshCartLine(line, item) {
    var lineTotal = line.querySelector('[data-line-total]');
    if (lineTotal && item) lineTotal.textContent = formatMoney(item.final_line_price);
  }

  function initAjaxCart() {
    var cartRoot = document.querySelector('[data-cart-page]');
    if (!cartRoot) return;

    function setSubtotal(cart) {
      var subtotalEl = cartRoot.querySelector('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      var countEl = cartRoot.querySelector('[data-cart-item-count]');
      if (countEl) countEl.textContent = cart.item_count;
      updateCartCount(cart.item_count);

      if (cart.item_count === 0) {
        window.location.reload();
      }
    }

    function changeLine(key, quantity, line, fallbackUrl) {
      line.classList.add('is-loading');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart-change-failed');
          return r.json();
        })
        .then(function (cart) {
          if (quantity === 0) {
            line.remove();
          } else {
            var item = cart.items.find(function (i) {
              return i.key === key;
            });
            refreshCartLine(line, item);
          }
          setSubtotal(cart);
        })
        .catch(function () {
          // Fallback: for a removal, follow Shopify's own remove link so the
          // result is correct even though the AJAX call failed. For a
          // quantity change, submit the native form — its inputs already
          // hold the values the customer set, including this one.
          if (fallbackUrl) {
            window.location.href = fallbackUrl;
            return;
          }
          var form = cartRoot.querySelector('form[action*="/cart"]');
          if (form) form.submit();
        })
        .finally(function () {
          line.classList.remove('is-loading');
        });
    }

    cartRoot.addEventListener('click', function (event) {
      var minus = event.target.closest('[data-qty-minus]');
      var plus = event.target.closest('[data-qty-plus]');
      var remove = event.target.closest('[data-line-remove]');

      if (minus || plus) {
        var line = event.target.closest('[data-cart-line]');
        var input = line && line.querySelector('[data-qty-input]');
        if (!input) return;
        var value = parseInt(input.value, 10) || 1;
        value = plus ? value + 1 : Math.max(0, value - 1);
        input.value = value;
        changeLine(line.getAttribute('data-line-key'), value, line);
      }

      if (remove) {
        event.preventDefault();
        var removeLine = event.target.closest('[data-cart-line]');
        if (removeLine) changeLine(removeLine.getAttribute('data-line-key'), 0, removeLine, remove.getAttribute('href'));
      }
    });

    cartRoot.addEventListener('change', function (event) {
      var input = event.target.closest('[data-qty-input]');
      if (!input) return;
      var line = input.closest('[data-cart-line]');
      var value = Math.max(0, parseInt(input.value, 10) || 0);
      changeLine(line.getAttribute('data-line-key'), value, line);
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initReveal();
    initStickyHeader();
    initAjaxAddToCart();
    initAjaxCart();
  });
})();
